/**
 * Document preview — what ProDoc read, beside the file it read it from.
 *
 * Opened from Upload Data after a run, for every file the run sent to ProDoc
 * (and every invoice a split PDF became); or from the upload history, for any
 * file ProDoc read. When there is more than one, they are listed on the left
 * and one is open at a time.
 *
 * The STRUCTURE is the ProDoc review screen's — supplier, buyer, document,
 * line items, totals, with the original beside them. The look is this app's
 * (constitution §7): `@fiveox/ui` and the `gst-*` classes, nothing else.
 *
 * It decides nothing (ADR-0014). Corrections go through the same review
 * calls the Review screen makes, so they are audited the same way and show on
 * both; which fields may be corrected is the review body's answer, never a
 * list kept here.
 *
 * **A header field is corrected on every line at once, or on none** (D-66).
 * Each line carries its own copy of the supplier, the buyer and the invoice
 * number, and the return is built from lines, so a header correction has to
 * reach every line of the document. This screen first did that one request
 * per line: a failure half-way left the document disagreeing with itself,
 * and it reached only the lines loaded on the page. It now makes one call,
 * `corrections/every-row` (W3-13 stage 2), which corrects every live line in
 * one transaction, each with its own audited correction. When a box may be
 * edited is `headerEditable`'s rule. A line's own fields and its amounts are
 * one row each, so they are corrected here exactly as on Raw Data.
 *
 * **The file comes from ProGST, not from the object store** (2026-10-06).
 * The pane used to open a short-lived link to the store; on UAT that address
 * answered with the FiveOX sign-in page, so the pane showed a sign-in form
 * beside the fields. The bytes now come through ProGST's own API, signed in,
 * and are shown from a local copy, as Raw Data's pane does.
 */

import { Alert, Button, Card, buttonClass } from '@fiveox/ui'
import { ArrowLeft, ExternalLink, FileDown } from 'lucide-react'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DOCUMENT, useEvaluation } from '../lib/access'
import {
  ApiError,
  type DocumentRecord,
  type ReviewBody,
  type ReviewRow,
  columnMaps,
  documents,
  review,
} from '../lib/api'
import {
  LINE_COLUMNS,
  SECTIONS,
  type ColumnKind,
  type FieldSpec,
  cellText,
  charges,
  checksumOk,
  editText,
  APPROVAL_REMARK,
  TOTAL_HEADS,
  approvalState,
  bindingWords,
  fieldValue,
  headerEditable,
  nextToApprove,
  pdfFrameUrl,
  readByProgst,
  readMapping,
  rupees,
  totals,
  totalsVerdict,
} from '../lib/invoicePreview'
import { belowTotalNote } from '../lib/registerTotals'
import { isTrialBalance } from '../lib/trialBalance'
import { TrialBalancePane } from './TrialBalancePane'

/** One page of lines. A register can run to thousands; Review pages the rest. */
const LINE_PAGE = 200

interface Props {
  docs: DocumentRecord[]
  initialId: string
  /** Where a document stands, in the words Upload Data already uses. */
  whereLabel: (doc: DocumentRecord) => string
  onClose: () => void
}

interface Amounts {
  key: string
  values: Record<string, string>
  reason: string
  note: string
}

export function InvoicePreview({ docs, initialId, whereLabel, onClose }: Props) {
  const { can } = useEvaluation()
  const canEdit = can(DOCUMENT, 'can_update')

  const [selected, setSelected] = useState(initialId)
  const [detail, setDetail] = useState<DocumentRecord | null>(null)
  const [body, setBody] = useState<ReviewBody | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [rowsError, setRowsError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [amounts, setAmounts] = useState<Amounts | null>(null)
  // `url` is a local object URL for bytes ProGST sent, freed when replaced
  // (below). `id` is the document those bytes belong to: the pane shows them
  // only while that is still the selection, so not even the one frame between
  // a click and the reset below can caption one invoice's file with another's
  // name.
  const [source, setSource] = useState<{ id: string; url: string; render: string } | null>(null)
  const [sourceError, setSourceError] = useState<string | null>(null)
  // The approval's remark, pre-filled (owner, 2026-10-07), and what was approved here — the list
  // this screen was opened with was read before, so it cannot know.
  const [remark, setRemark] = useState(APPROVAL_REMARK)
  const [approvedHere, setApprovedHere] = useState<ReadonlySet<string>>(new Set())
  // A spreadsheet's bound columns (3d): the saved mapping's, found by the id the document records.
  const [columns, setColumns] = useState<Record<string, unknown> | null>(null)

  // The document on screen NOW. Every response checks it before landing, so a
  // slow answer for the previous selection can never fill this one's fields.
  const current = useRef(selected)
  current.current = selected

  const listed = useMemo(() => docs.find((d) => d.id === selected) ?? null, [docs, selected])
  const doc = detail ?? listed

  const loadRows = useCallback(async (id: string) => {
    try {
      const got = (await review.get(id, { limit: LINE_PAGE })).data
      if (current.current !== id) return
      setBody(got)
      setRowsError(null)
    } catch (err) {
      if (current.current !== id) return
      setBody(null)
      setRowsError(err instanceof ApiError ? err.message : 'The extracted rows could not be loaded.')
    }
  }, [])

  // Everything about the previous document is dropped the moment the
  // selection moves — above all the file URL, which would otherwise show the
  // PREVIOUS invoice under this one's name (the same rule as Review's pane).
  useEffect(() => {
    let live = true
    setDetail(null)
    setBody(null)
    setLoadError(null)
    setRowsError(null)
    setSaveError(null)
    setAmounts(null)
    setSource(null)
    setSourceError(null)
    setRemark(APPROVAL_REMARK)
    setColumns(null)
    void (async () => {
      try {
        const got = (await documents.get(selected)).data
        if (live) setDetail(got)
      } catch (err) {
        if (live) setLoadError(err instanceof ApiError ? err.message : 'This document could not be loaded.')
      }
    })()
    void loadRows(selected)
    return () => {
      live = false
    }
  }, [selected, loadRows])

  const openSource = useCallback(async () => {
    const id = selected
    try {
      const file = await documents.sourceFile(id)
      // Checked BEFORE the local copy is made: an answer for a document no
      // longer selected is dropped whole, and leaves nothing behind to free.
      if (current.current !== id) return
      setSource({ id, url: URL.createObjectURL(file.blob), render: file.render ?? 'none' })
      setSourceError(null)
    } catch (err) {
      if (current.current !== id) return
      setSource(null)
      setSourceError(err instanceof ApiError ? err.message : 'The file could not be opened.')
    }
  }, [selected])

  useEffect(() => {
    if (!source && !sourceError) void openSource()
  }, [source, sourceError, openSource])

  // A shown file is a local copy of the bytes; free it when another selection
  // clears it, or when the screen closes. Without this
  // every invoice clicked through stays in the tab's memory until it closes.
  useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source.url)
    },
    [source],
  )

  // Only ever this selection's file (see `source` above).
  const shown = source && source.id === selected ? source : null

  /** Every write goes through here: one at a time, errors shown, rows re-read. */
  const write = useCallback(
    async (fn: () => Promise<unknown>) => {
      setSaving(true)
      setSaveError(null)
      try {
        await fn()
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.message : 'The correction was not saved.')
      } finally {
        setSaving(false)
        await loadRows(selected)
      }
    },
    [loadRows, selected],
  )

  const rows = body?.rows ?? []
  const normalized = detail?.normalized ?? null
  const correctable = new Set(body?.correctable_fields ?? [])
  const amountFields = [...(body?.money_fields ?? []), ...(body?.rate_fields ?? [])]

  const headerState = { canEdit, saving, correctable, rows, totalRows: body?.total_rows ?? 0 }

  /** Every live line of the document, in one call, or none (D-66). */
  const saveHeader = (field: string, value: string) =>
    write(() => review.correctEveryRow(selected, { field, value }))

  const rowEditable = (row: ReviewRow) => canEdit && !saving && !row.excluded_by

  const hasLiveLines =
    rows.some((row) => !row.excluded_by) || (body !== null && body.total_rows > rows.length)
  // A trial balance is approved for its rows, lines or none: a month whose rows are all reported
  // books none, and still awaits its approval every month (W3-17 (8d)).
  const trialBalance = doc ? isTrialBalance(doc) : false
  const approval = doc && body ? approvalState(doc, hasLiveLines || trialBalance) : null

  /**
   * Approve this invoice: the verification record, who and when and in what words (D-65). With
   * `GST_REQUIRE_APPROVAL` on it is what lets a return use the document (ADR-0035). Then on to the
   * next one still to approve, so a run of invoices is approved in one sitting.
   */
  const approve = useCallback(async () => {
    const id = selected
    setSaving(true)
    setSaveError(null)
    try {
      await review.verify(id, { remark: remark.trim() || APPROVAL_REMARK })
      const done = new Set(approvedHere).add(id)
      setApprovedHere(done)
      const next = nextToApprove(docs, id, done)
      if (next) {
        setSelected(next)
      } else if (current.current === id) {
        setDetail((await documents.get(id)).data)
      }
    } catch (err) {
      if (current.current === id) {
        setSaveError(err instanceof ApiError ? err.message : 'The approval was not saved.')
      }
    } finally {
      setSaving(false)
    }
  }, [selected, remark, approvedHere, docs])

  // W3-13 (3d): a spreadsheet ProGST read itself is approved once, for the whole file, from its
  // columns and its own total row — not field by field against a page.
  const isSheet = doc ? readByProgst(doc) : false
  const mapping = isSheet ? readMapping(detail?.normalized) : null
  const mappingId = mapping?.kind === 'profile' ? mapping.id : null
  const clientId = doc?.client_id ?? null
  useEffect(() => {
    if (!mappingId || !clientId) return
    let live = true
    void columnMaps
      .list(clientId)
      .then((got) => {
        if (live) setColumns(got.data.maps.find((m) => m.id === mappingId)?.columns ?? null)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [mappingId, clientId])

  const confidence = doc?.confidence
  const checksum = doc ? checksumOk(doc, normalized) : null
  const totalLines = totals(normalized)
  const chargeLines = charges(normalized)

  return (
    <div>
      <button type="button" className="gst-link mb-2 inline-flex items-center gap-1 text-sm" onClick={onClose}>
        <ArrowLeft size={14} /> Upload Data
      </button>

      <header className="mb-4">
        <h1 className="gst-h1">{doc?.source_filename ?? 'Document'}</h1>
        {doc && (
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <span className={`gst-chip ${doc.held ? 'is-hold' : 'is-ok'}`}>{whereLabel(doc)}</span>
            {typeof confidence === 'number' && (
              <span className="gst-tag">{Math.round(confidence)}% confidence</span>
            )}
            {checksum !== null && (
              <span className={`gst-chip ${checksum ? 'is-ok' : 'is-stop'}`}>
                {checksum ? 'Totals add up' : 'Totals do not add up'}
              </span>
            )}
            <span className="gst-meta">
              {doc.doc_type_words} · {doc.read_by}
              {doc.period && <> · <span className="gst-mono">{doc.period}</span></>}
            </span>
          </p>
        )}
        {doc?.next_action && <p className="gst-sub mt-2">{doc.next_action}</p>}
        {doc && approval === 'approved' && doc.verified && (
          <Alert tone="success" className="mt-3">
            Approved {when(doc.verified.at)}
            {doc.verified.remark ? ` — ${doc.verified.remark}` : ''}
          </Alert>
        )}
        {doc && canEdit && approval === 'approvable' && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="gst-label min-w-64 flex-1">
              Remark
              <input
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                className="gst-input w-full"
                maxLength={500}
              />
            </label>
            <Button onClick={() => void approve()} disabled={saving}>
              {trialBalance ? 'Approve this trial balance' : isSheet ? 'Approve this file' : 'Approve this invoice'}
            </Button>
            <p className="gst-meta w-full">
              {trialBalance
                ? 'Approve once each row’s outcome is right and the sheets of the month are linked. Approving reads its notes: a noted row is booked once approved. It records who approved it and when.'
                : isSheet
                ? 'Approve once the columns and the totals are right. The approval covers every line of this file, and records who approved it and when.'
                : 'Approve once every field matches the file. The approval records who approved it and when.'}
            </p>
          </div>
        )}
        {doc && canEdit && approval === 'waits' && !doc.verified && (
          <p className="gst-meta mt-3">Approve is offered once the hold above is resolved.</p>
        )}
      </header>

      {loadError && <Alert tone="error" className="mb-4">{loadError}</Alert>}
      {saveError && <Alert tone="error" className="mb-4">{saveError}</Alert>}

      <div className="flex items-start gap-4">
        {/* -- the list, when the run produced more than one -------------------- */}
        {docs.length > 1 && (
          <nav aria-label="Parsed documents" className="w-56 shrink-0 space-y-2">
            <p className="gst-meta">
              {docs.length} documents
            </p>
            {docs.map((d) => (
              <button
                key={d.id}
                type="button"
                className="gst-rail-item"
                aria-current={d.id === selected ? 'true' : undefined}
                onClick={() => setSelected(d.id)}
              >
                <span className="block truncate font-medium">{d.source_filename ?? 'Untitled'}</span>
                <span className="block truncate text-xs">
                  {d.doc_type_words} · {whereLabel(d)}
                </span>
              </button>
            ))}
          </nav>
        )}

        {/* -- the file ProDoc read ----------------------------------------------- */}
        {trialBalance && doc && body?.trial_balance ? (
          <section className="min-w-0 flex-1">
            <TrialBalancePane
              tb={body.trial_balance}
              clientId={doc.client_id}
              canEdit={canEdit}
              onChanged={() => loadRows(selected)}
            />
          </section>
        ) : isSheet && doc ? (
          <section className="min-w-0 flex-1">
            <SheetPane
              doc={doc}
              mapping={mapping}
              columns={columns}
              fieldWords={body?.field_words ?? {}}
              totals={body?.register_totals ?? null}
              download={shown?.url ?? null}
            />
          </section>
        ) : (
        <section className="min-w-0 flex-1">
          <Card className="sticky top-4 flex h-[calc(100vh-8rem)] flex-col p-3">
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
              <p className="gst-meta min-w-0 truncate">{doc?.source_filename}</p>
              {/* The pane shows the page alone (`pdfFrameUrl`); zoom, print and
                  download are the full viewer's, one click away. The new tab
                  opens the same local copy, so it shows the file for as long
                  as it stays open; reloading that tab after this screen has
                  moved on finds the copy freed. */}
              {shown && (shown.render === 'pdf' || shown.render === 'image') && (
                <a
                  href={shown.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="gst-link inline-flex shrink-0 items-center gap-1 text-xs"
                >
                  <ExternalLink size={12} /> Open in new tab
                </a>
              )}
            </div>
            {sourceError ? (
              <div className="gst-empty">
                {sourceError}{' '}
                <button type="button" className="gst-link" onClick={() => void openSource()}>
                  Reload
                </button>
              </div>
            ) : !shown ? (
              <div className="gst-empty">Opening…</div>
            ) : shown.render === 'pdf' ? (
              <iframe
                title={doc?.source_filename ?? 'Uploaded document'}
                src={pdfFrameUrl(shown.url)}
                referrerPolicy="no-referrer"
                className="block min-h-0 w-full flex-1 rounded border-0"
              />
            ) : shown.render === 'image' ? (
              <div className="min-h-0 flex-1 overflow-auto rounded">
                <img
                  alt={doc?.source_filename ?? 'Uploaded document'}
                  src={shown.url}
                  referrerPolicy="no-referrer"
                  className="mx-auto block max-w-full"
                />
              </div>
            ) : (
              // A spreadsheet is never put in an iframe: served inline, the
              // browser downloads it, which looks like a spontaneous download.
              <div className="gst-empty flex-1">
                <p className="mb-2">This file type cannot be shown here — the fields beside it are what to check.</p>
                <a
                  href={shown.url}
                  download={doc?.source_filename ?? undefined}
                  referrerPolicy="no-referrer"
                  className={buttonClass({ variant: 'outline', size: 'sm', className: 'inline-flex' })}
                >
                  <FileDown size={13} /> Download
                </a>
              </div>
            )}
          </Card>
        </section>
        )}

        {/* -- what ProDoc read ------------------------------------------------------ */}
        <section className="min-w-0 flex-1 space-y-4">
          {trialBalance ? (
            <div>
              <p className="gst-panel-h">The lines from the trial balance</p>
              <p className="gst-meta">
                One line for each row that books one, worked out from the rows beside. A correction to a
                line here is recorded exactly as one made on Raw Data, and stays with its row.
              </p>
            </div>
          ) : isSheet ? (
            <div>
              <p className="gst-panel-h">The lines read</p>
              <p className="gst-meta">
                Check the columns and the totals beside, and the lines below. A correction to a line here is
                recorded exactly as one made on Raw Data.
              </p>
            </div>
          ) : (
          <>
          <div>
            <p className="gst-panel-h">Extracted data</p>
            <p className="gst-meta">
              Check each field against the file. A correction to a line here is recorded exactly as one
              made on Raw Data.
            </p>
            <p className="gst-meta mt-1">
              A supplier, buyer or document field is corrected on every line of the document at once. Where
              the lines disagree it reads <em>Varies by line</em>; correct those lines on Raw Data.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {SECTIONS.slice(0, 2).map((section) => (
              <Card key={section.title} className="space-y-3 p-4">
                <p className="gst-panel-h">{section.title}</p>
                {section.fields.map((spec) => {
                  const { value, varies } = fieldValue(spec, rows, normalized)
                  return (
                    <FieldBox
                      key={spec.label}
                      spec={spec}
                      value={value}
                      varies={varies}
                      editable={headerEditable(spec, varies, headerState)}
                      onSave={(v) => (spec.line ? saveHeader(spec.line, v) : Promise.resolve())}
                    />
                  )
                })}
              </Card>
            ))}
          </div>

          {SECTIONS.slice(2).map((section) => (
            <Card key={section.title} className="p-4">
              <p className="gst-panel-h mb-3">{section.title}</p>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="gst-label w-full">
                  Document type
                  <span className="gst-input is-fixed w-full">{doc?.doc_type_words ?? '—'}</span>
                </label>
                {section.fields.map((spec) => {
                  const { value, varies } = fieldValue(spec, rows, normalized)
                  return (
                    <FieldBox
                      key={spec.label}
                      spec={spec}
                      value={value}
                      varies={varies}
                      editable={headerEditable(spec, varies, headerState)}
                      onSave={(v) => (spec.line ? saveHeader(spec.line, v) : Promise.resolve())}
                    />
                  )
                })}
              </div>
            </Card>
          ))}

          </>
          )}

          {/* -- line items ------------------------------------------------------------ */}
          <Card className="p-4">
            <p className="gst-panel-h mb-3">
              Line items
              {body && <span className="gst-tag">{body.total_rows}</span>}
            </p>
            {rowsError ? (
              <div className="gst-empty">{rowsError}</div>
            ) : !body ? (
              <div className="gst-empty">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="gst-empty">ProDoc read no line items from this file.</div>
            ) : (
              <>
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th>Sr</th>
                        {LINE_COLUMNS.map((c) => (
                          <th key={c.field} className={c.kind === 'text' ? 'is-text' : undefined}>
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <LineRow
                          key={row.content_key}
                          index={body.offset + i}
                          row={row}
                          editable={rowEditable(row)}
                          correctable={correctable}
                          amountFields={amountFields}
                          amounts={amounts}
                          setAmounts={setAmounts}
                          body={body}
                          saving={saving}
                          onText={(field, value) =>
                            write(() => review.correct(selected, { content_key: row.content_key, field, value }))
                          }
                          onAmounts={(a) =>
                            write(async () => {
                              await review.correctMoney(selected, {
                                content_key: a.key,
                                values: Object.fromEntries(
                                  Object.entries(a.values).filter(([, v]) => v.trim()),
                                ),
                                reason_code: a.reason,
                                note: a.note,
                              })
                              setAmounts(null)
                            })
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                {body.total_rows > rows.length && (
                  <p className="gst-meta mt-2">
                    Showing the first {rows.length.toLocaleString()} of {body.total_rows.toLocaleString()} lines
                    — the rest are on Raw Data.
                  </p>
                )}
              </>
            )}
          </Card>

          {/* -- totals --------------------------------------------------------------- */}
          {!isSheet && (
          <Card className="p-4">
            <p className="gst-panel-h mb-3">Totals</p>
            {totalLines === null ? (
              <div className="gst-empty">ProDoc sent no totals for this document.</div>
            ) : (
              <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
                {totalLines.map((t) => (
                  <TotalRow key={t.label} label={t.label} value={rupees(t.paise)} />
                ))}
                {chargeLines.map((c, i) => (
                  <TotalRow
                    key={`charge-${i}`}
                    label={`${c.name}${c.included === false ? ' (outside the grand total)' : ''}`}
                    value={rupees(c.paise)}
                  />
                ))}
              </dl>
            )}
            <p className="gst-meta mt-3">
              Totals are as printed on the document and are not corrected here — correct the lines they
              come from.
            </p>
          </Card>
          )}
        </section>
      </div>
    </div>
  )
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="gst-mono text-right">{value}</dd>
    </>
  )
}

/**
 * A spreadsheet, as ProGST read it (W3-13 (3d)): the layout that read it and its bound columns, and
 * its own total row against the lines read. What a person approves a file on — the owner's
 * "these totals are right" (2026-10-05), not a page to compare field by field.
 */
function SheetPane({
  doc,
  mapping,
  columns,
  fieldWords,
  totals,
  download,
}: {
  doc: DocumentRecord
  mapping: ReturnType<typeof readMapping>
  columns: Record<string, unknown> | null
  fieldWords: Record<string, string>
  totals: ReviewBody['register_totals'] | null
  download: string | null
}) {
  const verdict = totalsVerdict(totals?.outcome)
  const below = belowTotalNote(totals?.below_total)
  return (
    <Card className="space-y-4 p-4">
      <div>
        <p className="gst-panel-h">The file, as read</p>
        <p className="gst-meta">{doc.source_filename}</p>
      </div>

      {mapping ? (
        <p className="text-sm">
          {mapping.kind === 'profile' ? (
            <>
              Read with the column layout <b>{mapping.name}</b>
            </>
          ) : (
            <>
              Read as a <b>{mapping.platform}</b> settlement report
            </>
          )}
          {mapping.sheet && <> — sheet “{mapping.sheet}”</>}, headings on row {mapping.headerRow}.
          {mapping.kind === 'profile' && mapping.movedFrom && (
            <>
              {' '}The layout was saved for sheet “{mapping.movedFrom.sheet}”, headings on row{' '}
              {mapping.movedFrom.headerRow}; this file has the same headings here.
            </>
          )}
          {mapping.kind === 'profile' && mapping.proven && (
            <>
              {' '}No person bound it: every key column was found once, and its rows add up to the
              sheet&rsquo;s own total row. Check the columns below before approving.
            </>
          )}
        </p>
      ) : (
        <p className="gst-meta">How this file was read is not recorded on it.</p>
      )}

      {columns && Object.keys(columns).length > 0 && (
        <div>
          <p className="gst-panel-h mb-2">Columns</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {Object.entries(columns).map(([field, binding]) => (
              <Fragment key={field}>
                <dt className="text-muted-foreground">{fieldWords[field] ?? field}</dt>
                <dd>← {bindingWords(binding)}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      )}

      <div>
        <p className="gst-panel-h mb-2">Against the file's own total row</p>
        <Alert tone={verdict.tone === 'ok' ? 'success' : verdict.tone === 'stop' ? 'error' : 'info'} className="mb-2">
          {verdict.words}
        </Alert>
        {totals?.attested && totals.summed && (
          <div className="gst-table-wrap">
            <table className="gst-table">
              <thead>
                <tr>
                  <th className="is-text">Head</th>
                  <th>The file's total</th>
                  <th>The lines read</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {TOTAL_HEADS.filter((h) => h in totals.attested! || h in totals.summed!).map((h) => (
                  <tr key={h}>
                    <td className="is-text">{fieldWords[h] ?? h}</td>
                    <td>{rupees(totals.attested![h] ?? null)}</td>
                    <td>{rupees(totals.summed![h] ?? null)}</td>
                    <td>{rupees(totals.deltas?.[h] ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {below && <p className="gst-meta mt-2">{below}</p>}
      </div>

      {download && (
        <a
          href={download}
          download={doc.source_filename ?? undefined}
          className={buttonClass({ variant: 'outline', size: 'sm', className: 'inline-flex' })}
        >
          <FileDown size={13} /> Download the file
        </a>
      )}
    </Card>
  )
}

/** An approval's moment, as a person reads it. */
function when(at: string): string {
  const d = new Date(at)
  return Number.isNaN(d.getTime())
    ? at
    : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * One header field. Editable, it saves on Enter or on leaving the box, to every line at once
 * (`saveHeader`); an emptied box puts the value back, since clearing is not a correction the
 * service takes. Shown, a value the lines disagree on reads "Varies by line", never the first
 * line's value presented as the document's.
 */
function FieldBox({
  spec,
  value,
  varies,
  editable,
  onSave,
}: {
  spec: FieldSpec
  value: string
  varies: boolean
  editable: boolean
  onSave: (value: string) => Promise<unknown>
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])

  const commit = () => {
    const next = draft.trim()
    if (!next || next === value) {
      setDraft(value)
      return
    }
    void onSave(next)
  }

  return (
    <label className="gst-label w-full">
      {spec.label}
      {editable ? (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') setDraft(value)
          }}
          className="gst-input w-full"
        />
      ) : (
        <span
          className={`gst-input is-fixed w-full ${spec.multiline ? 'whitespace-pre-line' : ''}`}
          title={varies ? 'The lines of this document carry different values' : undefined}
        >
          {varies ? 'Varies by line' : value || '—'}
        </span>
      )}
    </label>
  )
}

/**
 * A text cell on one line, saved on Enter or on leaving the box. One row, one
 * request: nothing to half-apply, so it is corrected here as on Raw Data. An
 * emptied cell puts the value back — clearing is not a correction the service
 * takes.
 */
function TextCell({
  value,
  editable,
  onSave,
  minWidth = 'min-w-20',
}: {
  value: string
  editable: boolean
  onSave: (value: string) => Promise<unknown>
  /** The column's least width (`LineColumn.minWidth`). */
  minWidth?: string
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  if (!editable) return <>{value || '—'}</>
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim()
        if (!next || next === value) setDraft(value)
        else void onSave(next)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') setDraft(value)
      }}
      className={`gst-input w-full ${minWidth}`}
    />
  )
}

/**
 * One line. Text fields are corrected in place. Amounts, the rate and the
 * quantity are money corrections: the service takes them only with a reason
 * and a note, so clicking one opens the row's amounts, as on Review.
 */
function LineRow({
  index,
  row,
  editable,
  correctable,
  amountFields,
  amounts,
  setAmounts,
  body,
  saving,
  onText,
  onAmounts,
}: {
  index: number
  row: ReviewRow
  editable: boolean
  correctable: ReadonlySet<string>
  amountFields: string[]
  amounts: Amounts | null
  setAmounts: (a: Amounts | null) => void
  body: ReviewBody
  saving: boolean
  onText: (field: string, value: string) => Promise<unknown>
  onAmounts: (a: Amounts) => Promise<unknown>
}) {
  const open = amounts?.key === row.content_key
  const kindOf = (field: string): ColumnKind =>
    LINE_COLUMNS.find((c) => c.field === field)?.kind ?? (body.money_fields.includes(field) ? 'money' : 'count')

  return (
    <>
      <tr className={index % 2 === 1 ? 'is-a' : ''}>
        <td>
          {index + 1}
          {row.excluded_by && <small className="block">taken out</small>}
          {row.added_by && <small className="block">added by hand</small>}
        </td>
        {LINE_COLUMNS.map((c) => {
          const value = row.effective[c.field]
          if (c.kind === 'text') {
            return (
              <td key={c.field} className="is-text">
                <TextCell
                  value={value === null || value === undefined ? '' : String(value)}
                  editable={editable && correctable.has(c.field)}
                  onSave={(v) => onText(c.field, v)}
                  minWidth={c.minWidth}
                />
              </td>
            )
          }
          const shown = cellText(value, c.kind)
          return (
            <td key={c.field}>
              {editable && amountFields.includes(c.field) ? (
                <button
                  type="button"
                  className="gst-link"
                  title="Correct this line's amounts"
                  onClick={() => setAmounts({ key: row.content_key, values: {}, reason: '', note: '' })}
                >
                  {shown}
                </button>
              ) : (
                shown
              )}
            </td>
          )
        })}
      </tr>
      {open && amounts && (
        <tr>
          <td colSpan={LINE_COLUMNS.length + 1} className="is-text">
            <div className="flex flex-wrap gap-2">
              {amountFields.map((f) => (
                <label key={f} className="gst-label">
                  {body.field_words[f] ?? f}
                  <input
                    value={amounts.values[f] ?? ''}
                    onChange={(e) =>
                      setAmounts({ ...amounts, values: { ...amounts.values, [f]: e.target.value } })
                    }
                    placeholder={editText(row.effective[f], kindOf(f))}
                    className="gst-input w-24"
                  />
                </label>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select
                value={amounts.reason}
                onChange={(e) => setAmounts({ ...amounts, reason: e.target.value })}
                className="gst-select"
                aria-label="Why"
              >
                <option value="">Why?</option>
                {Object.entries(body.money_reasons).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                value={amounts.note}
                onChange={(e) => setAmounts({ ...amounts, note: e.target.value.slice(0, 500) })}
                placeholder="What does the document say?"
                className="gst-input min-w-56 flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  saving ||
                  !amounts.reason ||
                  !amounts.note.trim() ||
                  !Object.values(amounts.values).some((v) => v.trim())
                }
                onClick={() => void onAmounts(amounts)}
              >
                Save amounts
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setAmounts(null)}>
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
